import { useMemo } from "react";
import { Link as RouterLink } from "react-router-dom";
import { useRecordContext, FunctionField } from "react-admin";
import { Alert, Box, Divider, Link, Stack, Typography } from "@mui/material";
import PeopleOutline from "@mui/icons-material/PeopleOutline";
import { formatUSD } from "@/utils/formatters/currencyFormatter";
import { useAppTheme } from "@/hooks/useAppTheme";
import { PaginatedDatagrid } from "@/components/ui/react-admin/PaginatedDatagrid";
import { ListEmptyState } from "@/components/common/ListEmptyState";
import { KPIStatCard, type KPIItem } from "@/components/common/KPIStatCard";
import { StatusChip } from "@/components/common/fields/StatusChipField";
import { useSelectedPolicyId } from "@/resources/claimants/components/PolicyContext";
import type { AggregateBreachCalculation, AggregateBreachClaimantDetail } from "@/types";
import { useBreachClaimantDetails } from "./useBreachClaimantDetails";

export const BreachClaimantDetail = () => {
  const record = useRecordContext<AggregateBreachCalculation>();
  const policyId = useSelectedPolicyId();
  const { isDarkMode } = useAppTheme();
  const { details, isLoading, isError } = useBreachClaimantDetails(policyId, record?.id);

  // KPIStatCard.value is number | string only — boolean indicators render outside
  // the KPI grid via the status row below. Six numeric KPIs map cleanly to a 6-col grid.
  const summaryItems: KPIItem[] = useMemo(() => {
    if (!record) return [];
    return [
      { label: "Total Claimant Count", value: record.total_claimant_count },
      { label: "Total Agg Eligible", value: formatUSD(record.total_agg_eligible) },
      { label: "Breach Amount", value: formatUSD(record.breach_amount) },
      { label: "Carrier Liability", value: formatUSD(record.carrier_liability) },
      { label: "Net Carrier Liability", value: formatUSD(record.net_carrier_liability) },
      { label: "Total Accommodation Funded", value: formatUSD(record.total_accommodation_funded) },
    ];
  }, [record]);

  if (!record) return null;

  return (
    <Box
      sx={{
        p: 2,
        backgroundColor: isDarkMode ? "background.paper" : "background.default",
      }}
    >
      {isError ? (
        <Alert severity="error">Failed to load claimant details. Please refresh and try again.</Alert>
      ) : (
        <>
          <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1.5 }}>
            <Typography variant="subtitle2" color="text.secondary">
              Is Breached:
            </Typography>
            <StatusChip
              label={record.is_breached ? "Yes" : "No"}
              color={record.is_breached ? "error" : "success"}
              size="small"
            />
          </Stack>

          <KPIStatCard items={summaryItems} columns={6} loading={isLoading} skeletonCount={6} />

          <Divider sx={{ my: 1.5 }} />

          <PaginatedDatagrid
            data={details}
            isLoading={isLoading}
            initialPerPage={25}
            resource="aggregate-calculation-details"
            empty={
              <ListEmptyState
                icon={PeopleOutline}
                message="No claimant details"
                subtitle="This calculation has no qualifying claimants."
              />
            }
          >
            <FunctionField
              label="Claimant"
              render={(detail: AggregateBreachClaimantDetail) => (
                <Link component={RouterLink} to={`/claimants/${detail.claimant_id_ref}/show`} underline="hover">
                  {detail.claimant_id_ref.slice(0, 8)}…
                </Link>
              )}
            />
            <FunctionField
              label="Total Paid ($)"
              render={(detail: AggregateBreachClaimantDetail) => formatUSD(detail.claimant_total_paid)}
            />
            <FunctionField
              label="Effective Spec Deductible ($)"
              render={(detail: AggregateBreachClaimantDetail) => formatUSD(detail.effective_spec_deductible)}
            />
            <FunctionField
              label="Lasered Increment ($)"
              render={(detail: AggregateBreachClaimantDetail) => formatUSD(detail.lasered_increment)}
            />
            <FunctionField
              label="Is Lasered"
              render={(detail: AggregateBreachClaimantDetail) =>
                detail.is_lasered ? <StatusChip label="Lasered" color="warning" size="small" /> : null
              }
            />
            <FunctionField
              label="Agg Eligible ($)"
              render={(detail: AggregateBreachClaimantDetail) => formatUSD(detail.agg_eligible)}
            />
            <FunctionField label="Claim Count" render={(detail: AggregateBreachClaimantDetail) => detail.claim_count} />
          </PaginatedDatagrid>
        </>
      )}
    </Box>
  );
};
